import { useCallback, useEffect, useState } from "react";

import {
  AiTraceTurnList,
  GatewayConfigAuditEntry,
  aiGatewayService,
} from "services/aiGatewayService";

/** How many configuration changes the history table shows. The endpoint clamps to 1–200. */
const CONFIG_CHANGE_LIMIT = 50;

/**
 * Loads the per-turn trace list for Analytics → AI actions → Activity.
 *
 * Like `useAiCosts` and `useAiBudgets`, this stays out of `useAiGateway`: the trace query scans the
 * execution log and is not tied to the gateway feature — every tenant's turns are traced.
 *
 * The axios interceptor already fires an error toast on failure; `error` here only drives the
 * inline empty/error state.
 */
export function useAiTraces(
  days: number,
  page: number,
  pageSize: number,
  failuresOnly: boolean,
  refreshToken = 0,
) {
  const [turns, setTurns] = useState<AiTraceTurnList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reloadTurns = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await aiGatewayService.getTraces(
        days,
        page,
        pageSize,
        failuresOnly,
      );
      setTurns(loaded);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "load-failed");
    } finally {
      setLoading(false);
    }
    // `refreshToken` is not read here — it is the dependency that re-runs the same query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, page, pageSize, failuresOnly, refreshToken]);

  useEffect(() => {
    reloadTurns();
  }, [reloadTurns]);

  return { turns, loading, error, reloadTurns };
}

/**
 * Loads the gateway configuration-change history (AI Gateway → Current setup). Separate from the
 * turn list on purpose: the audit history does not change when someone pages through turns, so it
 * only re-reads on an explicit refresh.
 */
export function useGatewayConfigChanges(refreshToken = 0) {
  const [configChanges, setConfigChanges] = useState<GatewayConfigAuditEntry[]>(
    [],
  );
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setConfigChanges(
        await aiGatewayService.getConfigChanges(CONFIG_CHANGE_LIMIT),
      );
    } catch {
      // The interceptor has already told the user; the panel renders its empty state.
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { configChanges, loading, reload };
}
